/**
 * Single checklist room (Collapsible) with its task rows. Used so that the only
 * .map() in the tree is over tasks and returns <ChecklistTaskRow /> — no hooks
 * inside the map (fixes React #185).
 */
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ChevronDown, Camera, Upload, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Room, Task, getTaskText, normalizeTask } from "@/types/checklist";
import { ChecklistTaskRow } from "./ChecklistTaskRow";
import type { UploadedPhoto } from "./CleaningTab";
import type { IssueSeverity } from "./CleaningTab";
import type { MaintenanceResponseValue } from "./MaintenanceTaskUI";
import type { PropertyGuideEntry } from "@/hooks/usePropertyGuideEntries";
import { RoomInventoryPanel, type RoomInventoryItem } from "./RoomInventoryPanel";

interface ChecklistRoomRowProps {
  room: Room;
  originalRoom: Room | undefined;
  roomIndex: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  dir: "ltr" | "rtl";
  readOnly: boolean;
  getTaskKey: (roomName: string, taskText: string) => string;
  getTaskReferencePhotos: (roomName: string, taskText: string) => { id: string; photo_url: string; room_name: string; task_text: string | null }[];
  getReferencePhotoForRoom: (roomName: string) => { photo_url: string } | undefined;
  isTaskCompleted: (roomName: string, taskText: string) => boolean;
  onToggleTask: (roomName: string, taskText: string) => void;
  taskBeforePhotos: Record<string, UploadedPhoto[]>;
  taskAfterPhotos: Record<string, UploadedPhoto[]>;
  maintenanceResponses: Record<string, MaintenanceResponseValue | null>;
  maintenanceNotes: Record<string, string>;
  maintenancePhotos: Record<string, UploadedPhoto[]>;
  quickCheckVerified: Record<string, boolean>;
  quickCheckPhotos: Record<string, UploadedPhoto[]>;
  onMaintenanceResponseChange: (taskKey: string, response: MaintenanceResponseValue | null) => void;
  onMaintenanceNotesChange: (taskKey: string, noteText: string) => void;
  onMaintenanceAddPhotos: (taskKey: string, files: File[]) => void;
  onMaintenanceRemovePhoto: (taskKey: string, id: string) => void;
  onQuickCheckVerify: (taskKey: string) => void;
  onQuickCheckAddPhotos: (taskKey: string, files: File[]) => void;
  onQuickCheckRemovePhoto: (taskKey: string, id: string) => void;
  onAddBeforePhotos: (roomName: string, taskText: string, files: File[]) => void;
  onRemoveBeforePhoto: (roomName: string, taskText: string, id: string) => void;
  onAddAfterPhotos: (roomName: string, taskText: string, files: File[]) => void;
  onRemoveAfterPhoto: (roomName: string, taskText: string, id: string) => void;
  roomPhotos: Record<string, File | null>;
  roomIssues: Record<string, { hasIssue: boolean; description: string; photo: File | null; severity: IssueSeverity }>;
  onPhotoSelect: (roomName: string, file: File | null) => void;
  onToggleRoomIssue: (roomName: string, hasIssue: boolean) => void;
  onUpdateRoomIssueDescription: (roomName: string, description: string) => void;
  onUpdateRoomIssuePhoto: (roomName: string, file: File | null) => void;
  onUpdateRoomIssueSeverity: (roomName: string, severity: IssueSeverity) => void;
  tDynamic: (text: string) => string;
  guideEntries?: PropertyGuideEntry[];
  /** Inline inventory for this room (from shared inventory_rooms). */
  roomInventoryItems?: RoomInventoryItem[];
  onUpdateInventoryItem?: (params: { item_id: string; item_name: string; count?: number; fullness_value?: number }) => Promise<void>;
  onRequestOrder?: (item: RoomInventoryItem) => void;
  onPurchased?: (item: RoomInventoryItem) => void;
  /** When this returns true for an item id, that item already has a request_order for this turnover. */
  hasRequestOrderForItem?: (itemId: string) => boolean;
  /** When true, skip room-level photo + damage sections (shown in CleaningTab Photos/Issues tabs). */
  hideRoomPhotoAndIssueSection?: boolean;
  /** Next incomplete cleaning task key for subtle highlight (`room::task`). */
  nextFocusTaskKey?: string | null;
  /** When all tasks in this room are done, show a "next room" affordance with this localized name. */
  nextRoomLocalizedName?: string | null;
  /** Called when the cleaner taps "Go to next room" in the completion cue. */
  onJumpToNextRoom?: () => void;
}

export function ChecklistRoomRow({
  room,
  originalRoom,
  roomIndex,
  isExpanded,
  onToggleExpand,
  dir,
  readOnly,
  getTaskKey,
  getTaskReferencePhotos,
  getReferencePhotoForRoom,
  isTaskCompleted,
  onToggleTask,
  taskBeforePhotos,
  taskAfterPhotos,
  maintenanceResponses,
  maintenanceNotes,
  maintenancePhotos,
  quickCheckVerified,
  quickCheckPhotos,
  onMaintenanceResponseChange,
  onMaintenanceNotesChange,
  onMaintenanceAddPhotos,
  onMaintenanceRemovePhoto,
  onQuickCheckVerify,
  onQuickCheckAddPhotos,
  onQuickCheckRemovePhoto,
  onAddBeforePhotos,
  onRemoveBeforePhoto,
  onAddAfterPhotos,
  onRemoveAfterPhoto,
  roomPhotos,
  roomIssues,
  onPhotoSelect,
  onToggleRoomIssue,
  onUpdateRoomIssueDescription,
  onUpdateRoomIssuePhoto,
  onUpdateRoomIssueSeverity,
  tDynamic,
  guideEntries = [],
  roomInventoryItems,
  onUpdateInventoryItem,
  onRequestOrder,
  onPurchased,
  hasRequestOrderForItem,
  hideRoomInventoryPanel = false,
  hideRoomPhotoAndIssueSection = false,
  nextFocusTaskKey = null,
  nextRoomLocalizedName = null,
  onJumpToNextRoom,
}: ChecklistRoomRowProps) {
  const originalRoomName = originalRoom?.name || room.name;
  const localizedRoomName = room.name;
  const roomCoverPhoto = getReferencePhotoForRoom(originalRoomName);
  const tasks = room.tasks || [];
  const roomTasksCompleted = tasks.filter((t, ti) => {
    const origTask = originalRoom?.tasks?.[ti];
    const origTaskText = origTask ? getTaskText(origTask) : getTaskText(t);
    return isTaskCompleted(originalRoomName, origTaskText);
  }).length;
  const totalRoomTasks = tasks.length;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggleExpand} className="overflow-hidden rounded-2xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-card shadow-sm">
      <CollapsibleTrigger asChild>
        <button type="button" className="w-full text-left transition-colors hover:bg-muted/20 active:bg-muted/35">
          {roomCoverPhoto ? (
            <div className="relative">
              <img
                src={roomCoverPhoto.photo_url}
                alt={`${localizedRoomName} reference`}
                className="aspect-[2.4/1] max-h-[5.5rem] w-full object-cover sm:max-h-24"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-2 p-3 min-w-0">
                <span className="min-w-0 text-base font-bold tracking-tight text-white drop-shadow-sm truncate">
                  {localizedRoomName}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums text-white shadow-sm",
                      roomTasksCompleted >= totalRoomTasks && totalRoomTasks > 0
                        ? "bg-emerald-600/95"
                        : "bg-black/55 backdrop-blur-[2px]"
                    )}
                  >
                    {roomTasksCompleted >= totalRoomTasks && totalRoomTasks > 0 ? (
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    ) : null}
                    {roomTasksCompleted}/{totalRoomTasks}
                  </span>
                  <ChevronDown className={`h-5 w-5 text-white drop-shadow ${isExpanded ? "rotate-180" : ""}`} />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[52px] items-center justify-between gap-2 border-b border-[color:var(--pv-line,hsl(var(--border)))] bg-muted/30 p-3 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
                  {localizedRoomName}
                </span>
                <span className="text-muted-foreground text-sm shrink-0 tabular-nums font-medium">
                  {roomTasksCompleted}/{totalRoomTasks}
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`} />
            </div>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3 p-3 sm:space-y-4 sm:p-4" dir={dir}>
          {(room.entry_code?.trim() || room.entry_notes?.trim()) && (
            <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5 text-sm">
              <span className="font-medium text-foreground">{tDynamic("Code / access for this room")}</span>
              {room.entry_code?.trim() && (
                <p className="text-foreground">{room.entry_code.trim()}</p>
              )}
              {room.entry_notes?.trim() && (
                <p className="text-muted-foreground whitespace-pre-wrap">{room.entry_notes.trim()}</p>
              )}
            </div>
          )}
          <div className="divide-y divide-[color:var(--pv-divider,hsl(var(--border)))] overflow-hidden rounded-xl border border-[color:var(--pv-line,hsl(var(--border)))] bg-background">
            {tasks.map((task, taskIndex) => {
              const taskObj = normalizeTask(task);
              const origTask = originalRoom?.tasks?.[taskIndex];
              const origTaskObj = origTask ? normalizeTask(origTask as Task) : taskObj;
              const originalTaskText = origTaskObj.text;
              const displayTaskText = taskObj.text;
              const taskKey = getTaskKey(originalRoomName, originalTaskText);
              const taskRefPhotos = getTaskReferencePhotos(originalRoomName, originalTaskText);
              const hasRefPhotos = taskRefPhotos.length > 0;
              const showRefPhoto = !!origTaskObj.require_reference_photo && hasRefPhotos;
              const showBeforeUpload = !!origTaskObj.require_before_photo;
              const showAfterUpload = !!origTaskObj.require_after_photo;
              return (
                <ChecklistTaskRow
                  key={taskKey}
                  task={task}
                  taskIndex={taskIndex}
                  roomIndex={roomIndex}
                  originalRoom={originalRoom}
                  originalRoomName={originalRoomName}
                  displayTaskText={displayTaskText}
                  taskKey={taskKey}
                  readOnly={readOnly}
                  isTaskCompleted={isTaskCompleted}
                  onToggleTask={onToggleTask}
                  taskBeforePhotos={taskBeforePhotos}
                  taskAfterPhotos={taskAfterPhotos}
                  maintenanceResponses={maintenanceResponses}
                  maintenanceNotes={maintenanceNotes}
                  maintenancePhotos={maintenancePhotos}
                  quickCheckVerified={quickCheckVerified}
                  quickCheckPhotos={quickCheckPhotos}
                  taskRefPhotos={taskRefPhotos}
                  showRefPhoto={showRefPhoto}
                  showBeforeUpload={showBeforeUpload}
                  showAfterUpload={showAfterUpload}
                  onMaintenanceResponseChange={onMaintenanceResponseChange}
                  onMaintenanceNotesChange={onMaintenanceNotesChange}
                  onMaintenanceAddPhotos={onMaintenanceAddPhotos}
                  onMaintenanceRemovePhoto={onMaintenanceRemovePhoto}
                  onQuickCheckVerify={onQuickCheckVerify}
                  onQuickCheckAddPhotos={onQuickCheckAddPhotos}
                  onQuickCheckRemovePhoto={onQuickCheckRemovePhoto}
                  onAddBeforePhotos={onAddBeforePhotos}
                  onRemoveBeforePhoto={onRemoveBeforePhoto}
                  onAddAfterPhotos={onAddAfterPhotos}
                  onRemoveAfterPhoto={onRemoveAfterPhoto}
                  tDynamic={tDynamic}
                  guideEntries={guideEntries}
                  noRowDivider
                  isNextTask={taskKey === nextFocusTaskKey}
                />
              );
            })}
          </div>
          {totalRoomTasks > 0 && roomTasksCompleted === totalRoomTasks && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[color:var(--pv-teal,#0fa89a)]/30 bg-[color:var(--pv-teal-soft,rgba(15,168,154,0.08))] px-3 py-2.5"
              role="status"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-[color:var(--pv-teal-press,#0f766e)]">
                <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                <span>
                  {tDynamic("All tasks done in this room.")}
                </span>
              </div>
              {nextRoomLocalizedName && onJumpToNextRoom && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onJumpToNextRoom}
                  className="h-8 gap-1.5 border-[color:var(--pv-teal,#0fa89a)]/40 bg-background text-[color:var(--pv-teal-press,#0f766e)] hover:bg-[color:var(--pv-teal-soft,rgba(15,168,154,0.12))]"
                >
                  {tDynamic("Next room")}: {nextRoomLocalizedName}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
          {!hideRoomInventoryPanel &&
            roomInventoryItems != null &&
            roomInventoryItems.length > 0 &&
            onUpdateInventoryItem &&
            onRequestOrder &&
            onPurchased && (
            <div className="pt-3 border-t">
              <RoomInventoryPanel
                roomName={originalRoomName}
                items={roomInventoryItems}
                readOnly={readOnly}
                tDynamic={tDynamic}
                onUpdateItem={onUpdateInventoryItem}
                onRequestOrder={onRequestOrder}
                onPurchased={onPurchased}
                hasRequestOrderForItem={hasRequestOrderForItem}
              />
            </div>
          )}
          {!hideRoomPhotoAndIssueSection && (
          <>
          {/* Room-level photo upload */}
          <div className="pt-3 border-t">
            <Label className="text-sm text-muted-foreground mb-2 block">{tDynamic("Room Photo")}</Label>
            <label className="flex items-center justify-center gap-2 w-full h-12 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {roomPhotos[originalRoomName] ? tDynamic("Photo selected") + " ✓" : tDynamic("Upload cleaning photo")}
              </span>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => onPhotoSelect(originalRoomName, e.target.files?.[0] ?? null)}
                className="hidden"
                disabled={readOnly}
              />
            </label>
          </div>
          {/* Issue reporting */}
          <div className="pt-3 border-t space-y-3">
            <div>
              <Label className="text-sm font-medium mb-2 block">{tDynamic("Any damages in this room?")}</Label>
              <select
                className={cn(
                  "flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                )}
                value={roomIssues[originalRoomName]?.hasIssue ? "yes" : "no"}
                onChange={(e) => onToggleRoomIssue(originalRoomName, e.target.value === "yes")}
                disabled={readOnly}
              >
                <option value="no">{tDynamic("No")}</option>
                <option value="yes">{tDynamic("Yes")}</option>
              </select>
            </div>
            {roomIssues[originalRoomName]?.hasIssue && (
              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg space-y-3">
                <h4 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {tDynamic("Damage Details")}
                </h4>
                <div>
                  <Label className="text-xs text-amber-700 mb-1 block">{tDynamic("Severity")}</Label>
                  <select
                    className={cn(
                      "flex h-9 w-full items-center justify-between rounded-md border border-amber-300 bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                    value={roomIssues[originalRoomName]?.severity ?? "medium"}
                    onChange={(e) => onUpdateRoomIssueSeverity(originalRoomName, e.target.value as IssueSeverity)}
                    disabled={readOnly}
                  >
                    <option value="low">{tDynamic("Low")}</option>
                    <option value="medium">{tDynamic("Medium")}</option>
                    <option value="high">{tDynamic("High")}</option>
                    <option value="urgent">{tDynamic("Urgent")}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-amber-700 mb-1 block">{tDynamic("Describe the damage...")}</Label>
                  <Textarea
                    placeholder={tDynamic("Describe the damage...")}
                    value={roomIssues[originalRoomName]?.description ?? ""}
                    onChange={(e) => onUpdateRoomIssueDescription(originalRoomName, e.target.value)}
                    rows={2}
                    className="text-base bg-white"
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-700 mb-1 block">{tDynamic("Photo (optional)")}</Label>
                  <label className="flex items-center justify-center gap-2 w-full h-12 border border-amber-300 bg-white rounded-lg cursor-pointer hover:bg-amber-50 transition-colors">
                    <Camera className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      {roomIssues[originalRoomName]?.photo ? tDynamic("Add Photo") + " ✓" : tDynamic("Add Photo")}
                    </span>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => onUpdateRoomIssuePhoto(originalRoomName, e.target.files?.[0] ?? null)}
                      className="hidden"
                      disabled={readOnly}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
